import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

export const useMainStore = defineStore('main', () => {

    // State
    const sellingSidePlatforms = ref([]);
    const revenue = ref([]);
    const campaignInfo = ref([]);
    const currencyConversion = ref([]);
    const fileList = ref([]);
    const revenueLimits = ref({}); // Cache: { [ssp_cntr]: { powerbiCorrected, nautilusAbsolute } }
    const revenueMetadata = ref({
        powerbi: null,
        nautilus: null
    }); // Revenue data freshness tracking

    // Loading states
    const indicateLoading = ref(false);
    const indicateError = ref(false);
    const powerbiLoading = ref(false);
    const nautilusLoading = ref(false);

    // Alert state
    const alert = ref({
        showAlert: false,
        isError: false,
        alertText: ""
    });

    // Getters as computed properties
    const getSellingSidePlatform = computed(() => (sspName) => {
        return sellingSidePlatforms.value.find(ssp => ssp.ssp_name === sspName);
    });

    const getSellingSidePlatformsForMonth = computed(() => (year, month) => {
        return sellingSidePlatforms.value
            .filter(ssp => {
                // Must have PowerBI revenue > 0 for this month
                const hasPowerBIRevenue = revenue.value.some(r =>
                    r.rev_year === parseInt(year) &&
                    r.rev_month === parseInt(month) &&
                    r.rev_ssp === ssp.ssp_name &&
                    (r.rev_source === 'POWERBI' || r.rev_source === 'POWERBICORRECTED') &&
                    !r.rev_is_deleted &&
                    parseFloat(r.rev_n2) > 0
                );

                return hasPowerBIRevenue;
            })
            .sort((a, b) => a.ssp_name.localeCompare(b.ssp_name));
    });

    const getRevenueForSSP = computed(() => (year, month, sspName, source) => {
        return revenue.value
            .filter(r =>
                r.rev_year === parseInt(year) &&
                r.rev_month === parseInt(month) &&
                r.rev_ssp === sspName &&
                r.rev_source === source &&
                !r.rev_is_deleted // Exclude deleted records from calculations
            )
            .reduce((sum, rev) => sum + rev.rev_n2, 0);
    });

    const getRevenueForConnection = computed(() => (year, month, sspName, connectionName, source) => {
        return revenue.value
            .filter(r =>
                r.rev_year === parseInt(year) &&
                r.rev_month === parseInt(month) &&
                r.rev_ssp === sspName &&
                r.rev_source === source &&
                r.rev_connectionType === connectionName &&
                !r.rev_is_deleted // Exclude deleted records from calculations
            )
            .reduce((sum, rev) => sum + rev.rev_n2, 0);
    });

    const getRevenueForAllConnections = computed(() => (year, month, sspName, source) => {
        return revenue.value
            .filter(r =>
                r.rev_year === parseInt(year) &&
                r.rev_month === parseInt(month) &&
                r.rev_ssp === sspName &&
                r.rev_source === source
            )
            .sort((a, b) => new Date(b.rev_created_at) - new Date(a.rev_created_at));
    });

    const getCampaignInfoForSSP = computed(() => (year, month, sspName) => {
        return campaignInfo.value.filter(c =>
            c.rev_year == parseInt(year) &&
            c.rev_month == parseInt(month) &&
            c.cnt_name.split("-")[0] == sspName
        );
    });

    const getConnectionNamesForSellingSidePlatform = computed(() => (year, month, sspName) => {
        return [...new Set(
            revenue.value
                .filter(r =>
                    r.rev_year === parseInt(year) &&
                    r.rev_month === parseInt(month) &&
                    r.rev_ssp === sspName &&
                    (r.rev_source === "POWERBI" || r.rev_source === "NAUTILUS") && // Only types that actually exist this month, not historic INVOICE-only ones
                    !r.rev_is_deleted
                )
                .map(r => r.rev_connectionType[0])
        )].sort((a, b) => a.localeCompare(b));
    });

    const getYears = computed(() => () => {
        const uniqueYears = [...new Set(revenue.value.map(r => r.rev_year))];
        return uniqueYears.sort((a, b) => b - a); // Descending order
    });

    const getMonths = computed(() => (year) => {
        const monthNames = ["Januar", "Februar", "März", "April", "Mai", "Juni",
                          "Juli", "August", "September", "Oktober", "November", "Dezember"];

        const monthsForYear = revenue.value
            .filter(r => r.rev_year === parseInt(year))
            .map(r => r.rev_month);

        const uniqueMonths = [...new Set(monthsForYear)];

        // Always include current month if viewing current year
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth() + 1; // 1-based

        if (parseInt(year) === currentYear && !uniqueMonths.includes(currentMonth)) {
            uniqueMonths.push(currentMonth);
        }

        return uniqueMonths
            .sort((a, b) => a - b)
            .map(m => monthNames[m - 1]);
    });

    // Revenue limits getter
    const getRevenueLimitForSSP = computed(() => (sspId) => {
        return revenueLimits.value[sspId] || { powerbiCorrected: 50, nautilusAbsolute: 100 };
    });

    const getCurrencyConversion = computed(() => () => {
        return currencyConversion.value;
    });

    // Actions
    function setSellingSidePlatforms(data) {
        sellingSidePlatforms.value = data;
    }

    function setRevenue(data) {
        revenue.value = data;
    }

    function setCampaignInfo(data) {
        campaignInfo.value = data;
    }

    function setCurrencyConversion(data) {
        currencyConversion.value = data;
    }

    function setRevenueMetadata(data) {
        revenueMetadata.value = data;
    }

    function setRevenueLimitForSSP(sspId, limits) {
        revenueLimits.value[sspId] = limits;
    }

    function setPowerbiLoading(loading) {
        powerbiLoading.value = loading;
    }

    function setNautilusLoading(loading) {
        nautilusLoading.value = loading;
    }

    function updateFileList(data) {
        fileList.value = data;
    }

    function setIndicateLoading(loading) {
        indicateError.value = false;
        indicateLoading.value = loading;
    }

    function setIndicateError(error) {
        indicateError.value = error;
    }

    function showSuccessAlert(text) {
        alert.value.alertText = text;
        alert.value.isError = false;
        alert.value.showAlert = true;
        setTimeout(() => {
            alert.value.showAlert = false;
        }, 3000);
    }

    function showErrorAlert(text) {
        alert.value.alertText = text;
        alert.value.isError = true;
        alert.value.showAlert = true;
        setTimeout(() => {
            alert.value.showAlert = false;
        }, 3000);
    }

    return {
        // State
        sellingSidePlatforms,
        revenue,
        campaignInfo,
        currencyConversion,
        fileList,
        revenueLimits,
        revenueMetadata,
        indicateLoading,
        indicateError,
        powerbiLoading,
        nautilusLoading,
        alert,

        // Getters
        getSellingSidePlatform,
        getSellingSidePlatformsForMonth,
        getRevenueForSSP,
        getRevenueForConnection,
        getRevenueForAllConnections,
        getCampaignInfoForSSP,
        getConnectionNamesForSellingSidePlatform,
        getYears,
        getMonths,
        getRevenueLimitForSSP,
        getCurrencyConversion,

        // Actions
        setSellingSidePlatforms,
        setRevenue,
        setCampaignInfo,
        setCurrencyConversion,
        setRevenueMetadata,
        setRevenueLimitForSSP,
        setPowerbiLoading,
        setNautilusLoading,
        updateFileList,
        setIndicateLoading,
        setIndicateError,
        showSuccessAlert,
        showErrorAlert
    };
});